// What a task attachment may be.
//
// Onboarding tasks accept the formats a practice actually sends during implementation; every other
// staff task stays PDF-only, exactly as before. The same split is enforced server-side, keyed off
// the task's implementation (see suno-backend StaffTaskAttachmentSerializer.validate) — this copy
// exists so the common mistakes are caught before a file is uploaded, not to be the authority.
//
// Gated on the file's extension rather than its reported MIME type. Windows reports
// `application/octet-stream` for .zip and Office files often enough that a type-based check
// rejects perfectly good uploads — which is exactly what the old `file.type !== "application/pdf"`
// test would have done once these formats were allowed.

export const MAX_ATTACHMENT_SIZE_MB = 25;
export const MAX_ATTACHMENT_SIZE = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

export const PDF_ONLY_EXTENSIONS = [".pdf"];

export const EXTENDED_EXTENSIONS = [
  ".pdf",
  ".zip",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".heic",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".csv"
];

export type AttachmentKind = "pdf" | "image" | "archive" | "office" | "generic";

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".heic"];
const OFFICE_EXTENSIONS = [
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".csv"
];

export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot).toLowerCase();
}

export function allowedExtensions(extended: boolean): string[] {
  return extended ? EXTENDED_EXTENSIONS : PDF_ONLY_EXTENSIONS;
}

/** The `accept` attribute for the file input. */
export function acceptAttribute(extended: boolean): string {
  return extended ? EXTENDED_EXTENSIONS.join(",") : "application/pdf";
}

/** Wording under the drop zone, so the rule and the copy cannot drift apart. */
export function uploadHint(extended: boolean): string {
  return extended
    ? `PDF, Office, images and .zip — up to ${MAX_ATTACHMENT_SIZE_MB} MB`
    : `PDF files only — up to ${MAX_ATTACHMENT_SIZE_MB} MB`;
}

/** Why this file cannot be attached, or null when it can be. */
export function validateAttachment(file: File, extended: boolean): string | null {
  const extension = extensionOf(file.name);
  if (!allowedExtensions(extended).includes(extension)) {
    return extended
      ? `${extension || "Files without an extension"} can't be uploaded. Accepted: ${EXTENDED_EXTENSIONS.join(", ")}.`
      : "Only PDF files can be uploaded here.";
  }

  if (file.size > MAX_ATTACHMENT_SIZE) {
    return `Files must be ${MAX_ATTACHMENT_SIZE_MB} MB or smaller.`;
  }

  return null;
}

/** Which icon represents this file. Everything used to show a PDF icon, including .zip files. */
export function attachmentKind(filename: string): AttachmentKind {
  const extension = extensionOf(filename);
  if (extension === ".pdf") return "pdf";
  if (IMAGE_EXTENSIONS.includes(extension)) return "image";
  if (extension === ".zip") return "archive";
  if (OFFICE_EXTENSIONS.includes(extension)) return "office";
  return "generic";
}
